/**
 * Results Display Component
 * Shows all calculated ROI values with proper formatting
 */

import React from 'react'
import { CalculationResultData, Currency, UnitSystem } from '../types/calculator.types'
import {
  formatCurrencyWithConversion,
  formatCostPerArea,
  formatSimpleCurrency,
  shouldShowCurrencyConversion,
  getCurrencyConversionInfo,
  handleCurrencyDisplayError
} from '../utils/currencyDisplay'
import StateDisplay from './StateDisplay'
import FormulaTooltip from './FormulaTooltip'
import styles from './ResultsDisplay.module.css'
import '../styles/animations.css'

interface ResultsDisplayProps {
  data: CalculationResultData
  unitSystem: UnitSystem
  isLoading?: boolean
  error?: string
  className?: string
  prominence?: 'high' | 'medium' | 'low'
  animateEntry?: boolean
  position?: 'below-form' | 'sidebar'
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
  data,
  unitSystem,
  isLoading = false,
  error,
  className = '',
  prominence = 'medium',
  animateEntry = true,
  position = 'below-form'
}) => {
  // Format freight cost with currency conversion details
  const formatFreightCost = () => {
    if (!data) return 'N/A';
    
    try {
      const conversionInfo = getCurrencyConversionInfo(data);

      if (conversionInfo) {
        return formatCurrencyWithConversion({
          showConversion: true,
          originalAmount: conversionInfo.originalAmount,
          originalCurrency: conversionInfo.originalCurrency,
          convertedAmount: conversionInfo.convertedAmount,
          convertedCurrency: conversionInfo.convertedCurrency
        });
      } else {
        return formatSimpleCurrency(data.freightCost, data.freightCurrency || data.currency);
      }
    } catch (error) {
      return handleCurrencyDisplayError(error as Error, data.freightCost, data.currency);
    }
  };



  // Format currency amounts - maintain compatibility with existing tests
  const formatCurrency = (amount: number, currency: Currency = data?.currency || 'USD', showDecimals: boolean = false) => {
    const formattedNumber = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0
    }).format(amount)

    // Use the original format to maintain test compatibility
    const currencyPrefix = currency === 'CAD' ? 'CAD' : 'USD'
    const unitSuffix = showDecimals ? (unitSystem === 'imperial' ? '/sq.ft' : '/sq.m') : ''
    return `${currencyPrefix} ${formattedNumber}${unitSuffix}`
  }

  // Format cost per area with currency conversion support
  const formatCostPerAreaWithConversion = () => {
    if (!data) return 'N/A';
    
    try {
      // Use the final currency (after any conversion) for cost per area
      const finalCurrency = data.freightCurrency || data.currency;
      return formatCostPerArea(data.costPerArea, finalCurrency, unitSystem);
    } catch (error) {
      // Fallback to the original format if there's an error
      return formatCurrency(data.costPerArea, data.currency, true);
    }
  }

  // Generate dynamic class names based on props
  const containerClasses = [
    styles.container,
    styles[`prominence${prominence.charAt(0).toUpperCase() + prominence.slice(1)}`],
    styles[`position${position.charAt(0).toUpperCase() + position.slice(1).replace('-', '')}`],
    animateEntry ? styles.animateEntry : '',
    animateEntry ? 'slide-in-up' : '',
    'transition-all',
    className
  ].filter(Boolean).join(' ')

  if (error) {
    return (
      <div className={containerClasses}>
        <StateDisplay
          state="error"
          prominence={prominence}
          position={position}
          animateEntry={animateEntry}
          errorTitle="Calculation Error"
          errorMessage={error}
          errorActionText="Try Again"
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={containerClasses}>
        <StateDisplay
          state="loading"
          prominence={prominence}
          position={position}
          animateEntry={animateEntry}
          loadingMessage="Calculating ROI..."
        />
      </div>
    )
  }

  // Add null check for data
  if (!data) {
    return (
      <div className={containerClasses}>
        <StateDisplay
          state="empty"
          prominence={prominence}
          position={position}
          animateEntry={animateEntry}
          emptyTitle="Ready to Calculate"
          emptyMessage="Fill out the form above and click 'Calculate ROI' to see your results."
        />
      </div>
    )
  }

  return (
    <div
      className={containerClasses}
      role="region"
      aria-labelledby="results-heading"
      aria-live="polite"
      aria-atomic="true"
    >

      {/* Results heading with prominence-based styling */}
      <h2 
        id="results-heading" 
        className={`${styles.resultsHeading} ${styles[`heading${prominence.charAt(0).toUpperCase() + prominence.slice(1)}`]}`}
      >
        Your ROI Analysis
      </h2>


      {/* Cost Breakdown List */}
      <div className={`${styles.resultsSection} stagger-children`}>
        <h3 className={styles.sectionHeading}>Investment Breakdown</h3>
        <ul
          className={styles.costBreakdown}
          role="list"
          aria-label="Cost breakdown and investment details"
        >
          {/* Combined Hard-Cem Cost (new display) */}
          {data.hardcemTotalCost !== undefined ? (
            <li className={styles.output} role="listitem">
              <strong>Hard-Cem Total Cost (includes freight):</strong>
              <span lang="en" className={styles.costValue}>
                {formatCurrency(data.hardcemTotalCost)}
              </span>
            </li>
          ) : (
            // Fallback to separate costs for backward compatibility
            <>
              <li className={styles.output} role="listitem">
                <strong>Hard-Cem Material Cost:</strong>
                <span lang="en" className={styles.costValue}>
                  {formatCurrency(data.krytonMaterialCost)}
                </span>
              </li>
              <li
                className={styles.output}
                role="listitem"
                aria-describedby={shouldShowCurrencyConversion(data) ? "freight-conversion-details" : undefined}
              >
                <strong>Freight Cost Estimate:</strong>
                <span className={`${styles.freightCost} ${styles.costValue}`}>
                  <span lang="en">{formatFreightCost()}</span>
                  {shouldShowCurrencyConversion(data) && (
                    <span
                      id="freight-conversion-details"
                      className={styles.srOnly}
                      aria-label="Currency conversion details"
                    >
                      Original amount {data.originalFreightRate} {data.originalFreightCurrency} converted to {data.freightCost} {data.freightCurrency || data.currency}
                    </span>
                  )}
                  {data.destinationAddress && ` (to ${data.destinationAddress})`}
                </span>
              </li>
            </>
          )}

          <li className={styles.output} role="listitem">
            <strong>Cost per area (all-in):</strong>
            <span lang="en" className={styles.costValue}>
              {formatCostPerAreaWithConversion()}
            </span>
          </li>
        </ul>
      </div>

      {/* ROI Analysis Section */}
      <div className={`${styles.resultsSection} stagger-children`}>
        <h3 className={styles.sectionHeading}>ROI Analysis</h3>
        <ul className={styles.costBreakdown} role="list" aria-label="ROI analysis and savings">
          <li className={styles.output} role="listitem">
            <strong>Downtime Loss (per event):</strong>
            <span lang="en" className={`${styles.valueIndicator} ${data.downtimeLoss > 0 ? styles.negativeValue : styles.neutralValue}`}>
              {formatCurrency(data.downtimeLoss)}
            </span>
          </li>
          <li className={`${styles.output} ${styles.roiHighlight}`} role="listitem">
            <strong>
              Estimated ROI:
              <FormulaTooltip
                id="estimated-roi-formula"
                ariaLabel="Formula information for Estimated ROI"
                formula="Downtime Loss / Hard-Cem Material Cost"
                expandedFormula="Downtime Loss = Downtime Cost per Hour × Downtime Hours"
                note={data.downtimeLoss === 0 ? "ROI is not applicable for zero-incident scenarios" : undefined}
              />
            </strong>
            <span lang="en" className={`${styles.valueIndicator} ${data.downtimeLoss === 0 ? styles.neutralValue : styles.positiveValue}`}>
              {data.downtimeLoss === 0 ? "n/a (no incidents)" : `${(data.downtimeLoss / data.totalInvestment).toFixed(2)}x`}
            </span>
          </li>
        </ul>
      </div>

      {/* Savings Analysis Section */}
      <div className={`${styles.resultsSection} stagger-children`}>
        <h3 className={styles.sectionHeading}>Savings Analysis</h3>
        <ul className={styles.costBreakdown} role="list" aria-label="Savings and cost comparisons">
          <li className={styles.output} role="listitem">
            <strong>Total Resurfacing Cost:</strong>
            <span lang="en" className={`${styles.valueIndicator} ${styles.negativeValue}`}>
              {formatCurrency(data.totalResurfacingCost)}
            </span>
          </li>
          <li className={styles.output} role="listitem">
            <strong>Total Downtime Cost:</strong>
            <span lang="en" className={`${styles.valueIndicator} ${styles.negativeValue}`}>
              {formatCurrency(data.totalDowntimeCost)}
            </span>
          </li>
          <li className={styles.output} role="listitem">
            <strong>Lifetime Savings:</strong>
            <span lang="en" className={`${styles.valueIndicator} ${data.lifetimeSavings > 0 ? styles.positiveValue : styles.neutralValue}`}>
              {formatCurrency(data.lifetimeSavings)}
            </span>
          </li>
          <li className={styles.output} role="listitem">
            <strong>
              Annual Savings:
              <FormulaTooltip
                id="annual-savings-formula"
                ariaLabel="Formula information for Annual Savings"
                formula="Lifetime Savings / Facility Life"
                expandedFormula="Lifetime Savings = Total Cost Without Hard-Cem - (Hard-Cem Cost + Hard-Cem Resurfacing Cost + Downtime Cost)"
              />
            </strong>
            <span lang="en" className={`${styles.valueIndicator} ${data.annualSavings > 0 ? styles.positiveValue : styles.neutralValue}`}>
              {formatCurrency(data.annualSavings)}
            </span>
          </li>
        </ul>
      </div>



      {/* General Disclaimer */}
      {data.disclaimer && !shouldShowCurrencyConversion(data) && (
        <div className={styles.disclaimer} role="note">
          <p>{data.disclaimer}</p>
        </div>
      )}

      {/* Live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.srOnly}
      >
        Freight cost updated with {shouldShowCurrencyConversion(data) ? 'currency conversion' : 'current rates'}
      </div>
    </div>
  )
}

export default ResultsDisplay